import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Textarea from 'react-textarea-autosize';
import * as linkify from 'linkifyjs';
import {
  Button,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIgetDuplicate,
  APImergeDuplicate,
} from '../../../api/Files';

import DuplicatesFileListItem from './FileListItem';
import UploadFileAccess from '../../Upload/FileAccess';
import UploadFileCustomField from '../../Upload/FileCustomField';
import UploadFileDate from '../../Upload/FileDate';
import UploadFileTaxonomy from '../../Upload/FileTaxonomy';
import UploadFileAccessGroupsUsers from '../../Upload/FileAccessGroupsUsers';
import FileStatusSelect from '../../Upload/FileStatusSelect';
import ProcessingSpinner from '../../ProcessingSpinner';

import { sendTrackingEvent } from '../../../helpers';
import { triggerNativeEventFor } from '../../../helpers/Events';
import { userHasFeature } from '../../../helpers/Features';
import { getSetting } from '../../../helpers/Settings';

import ErrorBoundary from '../../ErrorBoundary';
import Loading from '../../Loading';
import LoadingSmall from '../../LoadingSmall';

const FileMergeModal = ({
  duplicateUuid,
  selectedFiles,
  handleClose,
  afterFileMerge,
  customFields,
  taxonomies,
  fileStatuses,
}) => {
  const [duplicate, setDuplicate] = useState(null);
  const [finalFile, setFinalFile] = useState(null);
  const [initialTerms, setInitialTerms] = useState([]);
  const [removedTerms, setRemovedTerms] = useState([]);
  const [merging, setMerging] = useState(false);
  const [done, setDone] = useState(false);
  const [mergingErrors, setMergingErrors] = useState({
    taxonomy: [],
    customFields: [],
    customFieldsUrls: [],
  });

  const intl = useIntl();

  const refs = [];
  refs.name = useRef();
  refs.description = useRef();

  useEffect(() => {
    sendTrackingEvent('duplicate-merge-opened');
  }, []);

  useEffect(() => {
    APIgetDuplicate(duplicateUuid).then((response) => {
      const allFiles = response.data.files.filter(f => (selectedFiles.includes(f.uuid)));
      const allTerms = window._.uniqBy(allFiles.reduce((terms, file) => (
        [...terms, ...file.terms]
      ), []), 'id');
      setInitialTerms(allTerms);

      const allowedGroups = allFiles.reduce((groups, file) => (
        [...groups, ...file.allowed_groups]
      ), []);
      const allowedUsers = allFiles.reduce((users, file) => (
        [...users, ...file.allowed_users]
      ), []);

      const allCollections = window._.uniqBy(allFiles.reduce((collections, file) => (
        [...collections, ...file.collections]
      ), []), 'uuid');

      setDuplicate(response.data);
      setFinalFile({
        ...allFiles[0],
        terms: allTerms,
        public: allFiles.filter(f => (f.public)).length > 0,
        allowed_groups: allowedGroups,
        allowed_users: allowedUsers,
        collections: allCollections,
      });
    });
  }, [duplicateUuid, selectedFiles]);

  const handleMergeValidate = () => {
    let isError = false;

    const taxonomyErrors = [];
    const customFieldsErrors = [];
    const customFieldsUrlsErrors = [];

    if (userHasFeature('custom-categories')) {
      taxonomies.forEach(t => {
        if (t.required &&
          !(finalFile.terms && finalFile.terms.some(term => term.taxonomy_id === t.id))) {
          isError = true;
          taxonomyErrors.push(t.id);
        }
      });
    }

    if (userHasFeature('custom-fields')) {
      customFields.forEach(cf => {
        if (cf.required &&
          !(finalFile.custom_fields.find(fileCF => (fileCF.id === cf.id))?.value)) {
          isError = true;
          customFieldsErrors.push(cf.id);
        }

        if (cf.type === 'url') {
          const fieldValue = finalFile.custom_fields.find(fileCF => (fileCF.id === cf.id))?.value;

          if (fieldValue && !linkify.test(fieldValue)) {
            isError = true;
            customFieldsUrlsErrors.push(cf.id);
          }
        }
      });
    }

    let statusError = false;
    if (userHasFeature('file-status') && getSetting('file_status_required')) {
      if (!finalFile.status) {
        isError = true;
        statusError = true;
      }
    }

    setMergingErrors({
      taxonomy: taxonomyErrors,
      customFields: customFieldsErrors,
      customFieldsUrls: customFieldsUrlsErrors,
      ...(statusError && { status: true }),
    });

    return isError;
  };

  const handleMerge = () => {
    const isError = handleMergeValidate();

    if (isError) return;

    setMerging(true);
    APImergeDuplicate(duplicate.uuid, {
      final_file: {
        ...finalFile,
        accessGroups: finalFile.allowed_groups,
        accessUsers: finalFile.allowed_users,
      },
      selected_files: selectedFiles,
    }).then((response) => {
      const { data } = response;
      afterFileMerge(data);
      setDone(true);
      setMerging(false);

      sendTrackingEvent('duplicate-merge-completed');
    });
  };

  const getModalTitle = () => (
    (duplicate && duplicate.file_type === 'image') ? intl.formatMessage({ defaultMessage: 'Image merge' }) : intl.formatMessage({ defaultMessage: 'File merge' })
  );

  const getFileListTitle = () => {
    if (duplicate.file_type === 'image') {
      return intl.formatMessage(
        { defaultMessage: 'Merging {fileCount} {fileCount, plural, one {image} other {images}}' },
        { fileCount: selectedFiles.length },
      );
    }
    return intl.formatMessage(
      { defaultMessage: 'Merging {fileCount} {fileCount, plural, one {file} other {files}}' },
      { fileCount: selectedFiles.length },
    );
  };

  const getSelectedFiles = () => (
    duplicate.files.filter(f => (selectedFiles.includes(f.uuid)))
  );

  const getFileStatus = statusId => (
    fileStatuses.find(fs => (fs.id === parseInt(statusId))) || { value: -1 }
  );

  const handleFormChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    const newFinalFile = { ...finalFile };
    if (name === 'fileAccess') {
      if (value === 'public') {
        newFinalFile.public = true;
      } else {
        newFinalFile.public = false;
      }
    } else if (name === 'fileStatus') {
      if (value === '') {
        newFinalFile.status = null;
      } else {
        newFinalFile.status = getFileStatus(value);
      }
    } else if (name === 'fileDate') {
      newFinalFile.original_datetime = value;
    } else {
      newFinalFile[name] = value;
    }

    setFinalFile(newFinalFile);
  };

  const handleCustomFieldChange = (customField) => {
    finalFile.custom_fields = finalFile.custom_fields.filter(cf => (cf.id !== customField.id));
    finalFile.custom_fields.push({
      id: customField.id,
      value: customField.value,
    });
    setFinalFile(finalFile);
  };

  const setCustomFieldRef = (customFieldId, ref) => {
    refs[`cf-${customFieldId}`] = {};
    refs[`cf-${customFieldId}`].current = ref;
  };

  const handleCopyField = (e) => {
    e.preventDefault();

    const field = e.target.getAttribute('data-field');
    const file = duplicate.files.find(f => (f.uuid === e.target.getAttribute('data-uuid')));
    if (file) {
      if (field === 'name') {
        refs.name.current.value = file.display_name;
        finalFile.name = file.display_name;
      }

      if (field === 'description') {
        refs.description.current.value = file.description;
        triggerNativeEventFor(refs.description.current, {
          event: 'change',
          value: file.description,
        });
        finalFile.description = file.description;
      }

      if (field === 'custom_field') {
        const customFieldId = parseInt(e.target.getAttribute('data-custom-field-id'));
        const customField = file.custom_fields.find(cf => (cf.id === customFieldId));
        if (customField) {
          refs[`cf-${customFieldId}`].current.value = customField.value;
          handleCustomFieldChange({
            id: customFieldId,
            value: customField.value,
          });
        } else {
          refs[`cf-${customFieldId}`].current.value = '';
          handleCustomFieldChange({
            id: customFieldId,
            value: '',
          });
        }
        triggerNativeEventFor(refs[`cf-${customFieldId}`].current, {
          event: 'change',
          value: refs[`cf-${customFieldId}`].current.value,
        });
      }

      setFinalFile(finalFile);
    }
  };

  const copyLinks = (field, id = null) => {
    let { files } = duplicate;
    if (field === 'custom_field') {
      files = files.filter(
        f => (f.custom_fields.filter(cf => (cf.id === id && cf.value)).length > 0),
      );
    } else if (field === 'name') {
      files = files.filter(f => (f.display_name));
    } else {
      files = files.filter(f => (f[field]));
    }

    if (files.length === 0) {
      return false;
    }

    return files.map((df, idx) => (
      <Fragment key={df.uuid}>
        <a
          href="#copy-file-name"
          data-uuid={df.uuid}
          data-field={field}
          data-custom-field-id={(field === 'custom_field' && id) ? id : undefined}
          onClick={handleCopyField}
        >
          {df.display_name}
        </a>
        {idx < files.length - 1 ? ', ' : ''}
      </Fragment>
    ));
  };

  const handleTaxonomyChange = (values, taxonomyId, removed = []) => {
    let newTerms = window._.uniqBy(finalFile.terms
      .filter(t => (parseInt(t.taxonomy_id) === parseInt(taxonomyId))).concat(values), 'id');
    if (removed) {
      removed.forEach((r) => {
        newTerms = newTerms.filter(nt => (parseInt(nt.id) !== parseInt(r.value)));
        removedTerms.push(r.value);
      });
    }
    finalFile.terms = finalFile.terms
      .filter(t => (parseInt(t.taxonomy_id) !== parseInt(taxonomyId)))
      .concat(newTerms);

    setFinalFile(finalFile);
    setRemovedTerms(removedTerms);
  };

  const handleSwitchGroup = (groupUuid) => {
    const group = finalFile.allowed_groups.find(ag => (ag === groupUuid));
    if (group) {
      setFinalFile({
        ...finalFile,
        allowed_groups: finalFile.allowed_groups.filter(ag => (ag !== groupUuid)),
      });
    } else {
      setFinalFile({
        ...finalFile,
        allowed_groups: finalFile.allowed_groups.concat([groupUuid]),
      });
    }
  };

  const handleSwitchUser = (userUuid) => {
    const user = finalFile.allowed_users.find(au => (au === userUuid));
    if (user) {
      setFinalFile({
        ...finalFile,
        allowed_users: finalFile.allowed_users.filter(au => (au !== userUuid)),
      });
    } else {
      setFinalFile({
        ...finalFile,
        allowed_users: finalFile.allowed_users.concat([userUuid]),
      });
    }
  };

  const hasMergingErrors = mergingErrors.taxonomy.length > 0 || mergingErrors.status;

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {getModalTitle()}
      </ModalHeader>
      <ModalBody className="modal-body-split">
        {(!(duplicate && finalFile)) ? (
          <Loading />
        ) : (
          <>
            <div className="modal-body-sidebar">
              <div className="modal-body-sidebar-header">
                <div className="merge-files-summary">
                  <h5 className="merge-files-summary-title">
                    {getFileListTitle()}
                  </h5>
                  {hasMergingErrors && (
                    <p className="merge-files-validation-error file-validation-error">
                      {intl.formatMessage({ defaultMessage: 'Metadata is required to merge.' })}
                    </p>
                  )}
                </div>
              </div>
              <ul className="merge-files-list">
                {getSelectedFiles().map(sf => (
                  <DuplicatesFileListItem
                    key={sf.uuid}
                    file={sf}
                  />
                ))}
              </ul>
            </div>
            <div className="modal-body-content">
              <div className="merge-files-content">
                {merging && (
                  <div className="center">
                    <h5>{intl.formatMessage({ defaultMessage: 'Merging - please wait...' })}</h5>
                    <LoadingSmall />
                  </div>
                )}
                {done && (
                  <div className="center">
                    <h5>{intl.formatMessage({ defaultMessage: 'The selected files have been merged!' })}</h5>
                    <p>
                      <Button
                        color="secondary"
                        onClick={handleClose}
                      >
                        {intl.formatMessage({ defaultMessage: 'Close' })}
                      </Button>
                      {' '}
                      <a
                        className="btn btn-primary"
                        href={`/files/${finalFile.uuid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {intl.formatMessage({ defaultMessage: 'Open the file' })}
                      </a>
                    </p>
                  </div>
                )}
                {(!merging && !done) && (
                  <Form
                    className="file-merge-form"
                  >
                    <FormGroup>
                      <Label for="name">
                        {duplicate.file_type === 'image' ?
                          intl.formatMessage({ defaultMessage: 'Add name for merged image' }) :
                          intl.formatMessage({ defaultMessage: 'Add name for merged file' })}
                      </Label>
                      <Input
                        type="text"
                        name="name"
                        id="name"
                        defaultValue={finalFile.name}
                        innerRef={refs.name}
                        onInput={handleFormChange}
                      />
                      {copyLinks('name') && (
                        <FormText color="muted">
                          {intl.formatMessage({ defaultMessage: 'Copy name from:' })} {copyLinks('name')}
                        </FormText>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label for="description">
                        {
                          duplicate.file_type === 'image' ?
                            intl.formatMessage({ defaultMessage: 'Add description for merged image' }) :
                            intl.formatMessage({ defaultMessage: 'Add description for merged file' })
                          }
                      </Label>
                      <Textarea
                        className="form-control"
                        name="description"
                        id="description"
                        defaultValue={finalFile.description}
                        ref={refs.description}
                        onInput={handleFormChange}
                      />
                      {copyLinks('description') && (
                        <FormText color="muted">
                          {intl.formatMessage({ defaultMessage: 'Copy description from:' })} {copyLinks('description')}
                        </FormText>
                      )}
                    </FormGroup>
                    <FormGroup row className="file-options">
                      <Col sm={4} className="file-status-field">
                        <FileStatusSelect
                          defaultValue={finalFile.status}
                          isEdit={true}
                          selectedFile={finalFile}
                          selectedFiles={[finalFile]}
                          handleChange={handleFormChange}
                          forceRequiredError={mergingErrors.status}
                          scope="upload"
                        />
                      </Col>
                      <Col sm={4}>
                        <UploadFileAccess
                          isEdit={true}
                          isLink={false}
                          selectedFile={finalFile}
                          selectedFiles={[finalFile]}
                          handleFormChange={handleFormChange}
                        />
                      </Col>
                      {userHasFeature('created-date-field') && (
                        <Col sm={4}>
                          <UploadFileDate
                            isEdit={true}
                            selectedFile={finalFile}
                            selectedFiles={[finalFile]}
                            handleFormChange={handleFormChange}
                          />
                        </Col>
                      )}
                    </FormGroup>
                    {customFields.map(customField => (
                      <UploadFileCustomField
                        key={`custom-field-${customField.id}`}
                        customField={customField}
                        selectedFiles={[finalFile]}
                        file={finalFile}
                        handleCustomFieldChange={handleCustomFieldChange}
                        title={duplicate.file_type === 'image' ?
                          intl.formatMessage({ defaultMessage: 'Add {name} for merged image' }, { name: customField.name.toLowerCase() }) :
                          intl.formatMessage({ defaultMessage: 'Add {name} for merged file' }, { name: customField.name.toLowerCase() })}
                        copyLinks={copyLinks}
                        setRef={setCustomFieldRef}
                        forceRequiredError={mergingErrors.customFields.includes(customField.id)}
                        forceUrlError={mergingErrors.customFieldsUrls.includes(customField.id)}
                      />
                    ))}
                    {(!finalFile.public) && (
                      <UploadFileAccessGroupsUsers
                        selectedGroups={finalFile.allowed_groups}
                        selectedUsers={finalFile.allowed_users}
                        skipDefaultCollaborators={true}
                        handleSwitchGroup={handleSwitchGroup}
                        handleSwitchUser={handleSwitchUser}
                      />
                    )}
                    {taxonomies.length > 0 && (
                      <div className="merge-file-taxonomies">
                        <hr />
                        {taxonomies.map(tax => (
                          <UploadFileTaxonomy
                            key={`tax-${tax.id}`}
                            taxonomy={tax}
                            selectedFiles={[finalFile]}
                            initialTermsForHints={
                              initialTerms.filter(t => (t.taxonomy_id === tax.id))
                            }
                            handleTaxonomyChange={handleTaxonomyChange}
                            forceRequiredError={mergingErrors.taxonomy.includes(tax.id)}
                          />
                        ))}
                      </div>
                    )}
                  </Form>
                )}
              </div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {!done && (
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={merging}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={merging}
              onClick={handleMerge}
            >
              <ProcessingSpinner show={merging} />
              {intl.formatMessage({ defaultMessage: 'Merge' })}
            </Button>
          </div>
        )}
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  customFields: state.Metadata.customFields,
  taxonomies: state.Metadata.taxonomies,
  fileStatuses: state.Files.statuses,
});

FileMergeModal.propTypes = {
  customFields: PropTypes.array.isRequired,
  taxonomies: PropTypes.array.isRequired,
  fileStatuses: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  duplicateUuid: PropTypes.string.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  afterFileMerge: PropTypes.func,
};

FileMergeModal.defaultProps = {
  afterFileMerge: () => {},
};

export default connect(mapStateToProps)(FileMergeModal);
